/**
 * ごく単純なハッシュルーティング。ルータを足すほどの画面数ではない。
 * 縦メニュー・メニューのシート・検索が同じ並び（仕事の種類ごと）を使う。
 */
import type { IconName } from "./ui/Icon.js";

export type Route =
  | { name: "home" }
  | { name: "notifications" }
  | { name: "readiness" }
  | { name: "list" }
  | { name: "register"; code: string; key?: string }
  | { name: "requests" }
  | { name: "requestNew" }
  | { name: "request"; id: string }
  | { name: "obligations" }
  | { name: "incidents" }
  | { name: "report" }
  | { name: "training" }
  | { name: "criteria"; code?: string }
  | { name: "settings"; focus?: string }
  | { name: "roles" }
  | { name: "governance"; tab?: string }
  | { name: "coverage" }
  | { name: "notFound" };

export type RouteName = Route["name"];

export function parseRoute(hash: string): Route {
  const [path = "", query] = hash.split("?");
  const q = new URLSearchParams(query ?? "");
  const reg = /^#\/registers\/([A-Z_]+)$/.exec(path);
  if (reg) return { name: "register", code: reg[1]!, key: q.get("key") ?? undefined };
  const req = /^#\/requests\/([0-9a-f-]{36})$/.exec(path);
  if (req) return { name: "request", id: req[1]! };
  switch (path) {
    // OIDC のコールバックは #/login?oidc=… に戻ってくる。ログイン後はホームにする
    case "": case "#": case "#/": case "#/login": return { name: "home" };
    case "#/notifications": return { name: "notifications" };
    case "#/readiness": return { name: "readiness" };
    case "#/registers": return { name: "list" };
    case "#/obligations": return { name: "obligations" };
    case "#/incidents": return { name: "incidents" };
    case "#/incidents/new": return { name: "report" };
    case "#/training": return { name: "training" };
    // H-03: 準備度画面の「あと何をすべきか」から評価基準の該当項目に直接移れるように、code を受け取る
    case "#/criteria": return { name: "criteria", code: q.get("code") ?? undefined };
    case "#/settings": return { name: "settings", focus: q.get("focus") ?? undefined };
    case "#/roles": return { name: "roles" };
    // 通知から利用者の一覧（承認待ち）へ直接開けるように、tab を受け取る
    case "#/governance": return { name: "governance", tab: q.get("tab") ?? undefined };
    case "#/coverage": return { name: "coverage" };
    case "#/requests": return { name: "requests" };
    case "#/requests/new": return { name: "requestNew" };
  }
  return { name: "notFound" };
}

export interface NavItem {
  href: string; routes: RouteName[]; label: string; icon: IconName; perm?: string;
  /** この役割を持つ人にだけ出す（perm より狭く絞るとき） */
  role?: string;
  /** M-03: 何かの役割を持つ人にだけ出す（役割の無い社員の仕事は報告・申請・受講・通知だけ） */
  needsRole?: true;
  /** M-03: 役割の無い社員にだけ出す（会社の取り組みを1項目で見せる） */
  staffOnly?: true;
}

/** メニュー・検索に出してよいか。権限の無い項目は出さない。 */
export function navItemVisible(it: NavItem, me: { roles: string[]; permissions: string[] } | null): boolean {
  if (it.perm && !me?.permissions.includes(it.perm)) return false;
  if (it.role && !me?.roles.includes(it.role)) return false;
  const hasRole = (me?.roles.length ?? 0) > 0;
  if (it.needsRole && !hasRole) return false;
  if (it.staffOnly && hasRole) return false;
  return true;
}
export interface NavGroup { label: string | null; items: NavItem[] }

/** 縦メニューの並び。仕事の種類でまとめる（業務／記録・台帳／年次サイクル／管理）。 */
export const NAV: NavGroup[] = [
  { label: null, items: [
    { href: "#/", routes: ["home"], label: "nav.home", icon: "home" },
    { href: "#/notifications", routes: ["notifications"], label: "notif.title", icon: "bell" },
  ] },
  { label: "navg.work", items: [
    { href: "#/requests", routes: ["requests", "requestNew", "request"], label: "nav.requests", icon: "doc" },
    { href: "#/incidents", routes: ["incidents", "report"], label: "nav.incidents", icon: "alert" },
    { href: "#/training", routes: ["training"], label: "nav.training", icon: "book" },
    { href: "#/obligations", routes: ["obligations"], label: "nav.obligations", icon: "clock", needsRole: true },
    // 役割の無い社員には、会社の取り組み（体制）を1項目で見せる
    { href: "#/roles", routes: ["roles"], label: "nav.companySecurity", icon: "users", staffOnly: true },
  ] },
  { label: "navg.records", items: [
    { href: "#/registers", routes: ["list", "register"], label: "nav.registers", icon: "table", needsRole: true },
    { href: "#/criteria", routes: ["criteria"], label: "nav.criteria", icon: "list", needsRole: true },
  ] },
  { label: "navg.cycle", items: [
    { href: "#/readiness", routes: ["readiness"], label: "nav.dashboard", icon: "chart", perm: "governance:read" },
  ] },
  { label: "navg.admin", items: [
    { href: "#/roles", routes: ["roles"], label: "nav.roles", icon: "users", needsRole: true },
    { href: "#/governance", routes: ["governance"], label: "nav.governance", icon: "shield", perm: "governance:read" },
    // M-34 / H-04: 「機能対応表」は開発の進捗表。利用者・CIO のメニューには出さず、システム管理者にだけ出す
    { href: "#/coverage", routes: ["coverage"], label: "nav.coverage", icon: "grid", role: "SYS_ADMIN" },
  ] },
];

/**
 * ページ見出しの上に出す区分名（縦メニューのグループ名）の i18n キー。無ければ null（ホーム・通知・設定など）。
 * 同じ画面が2つのグループに出る（役割一覧）ので、その人に見える項目の方を選ぶ。
 */
export function routeGroupKey(r: Route, me: { roles: string[]; permissions: string[] } | null): string | null {
  for (const g of NAV) {
    if (g.items.some((it) => it.routes.includes(r.name) && navItemVisible(it, me))) return g.label;
  }
  return null;
}

/** ブラウザのタブに出す画面名。 */
export function routeTitleKey(r: Route): string {
  for (const g of NAV) for (const it of g.items) if (it.routes.includes(r.name) && !it.staffOnly) {
    if (r.name === "report") return "inc.report";
    if (r.name === "requestNew") return "req.new";
    return it.label;
  }
  if (r.name === "settings") return "nav.settings";
  return "app.title";
}

/**
 * 今のハッシュのクエリ（#/obligations?answer=… の answer など）。通知・カレンダーの直リンクで使う。
 * ログインが要るときも URL のハッシュはそのまま残るので、ログイン後に同じ作業へ着地する。
 */
export function hashQuery(hash: string = location.hash): URLSearchParams {
  return new URLSearchParams(hash.split("?")[1] ?? "");
}

/** ハッシュのクエリを書き換える（履歴は増やさない）。共有したリンクで同じ表示になるように。 */
export function replaceHashQuery(update: Record<string, string | null>): void {
  const [path = "#/", q = ""] = location.hash.split("?");
  const params = new URLSearchParams(q);
  for (const [k, v] of Object.entries(update)) { if (v === null) params.delete(k); else params.set(k, v); }
  const s = params.toString();
  history.replaceState(history.state, "", `${location.pathname}${location.search}${path}${s ? `?${s}` : ""}`);
}
